import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { Lead } from "@/types"
import type { CreateCxAppointmentPayload } from "@/server/data/cx-appointments"
import type { TSendOfferSchema } from "@/schemas/forms"
import type { OfferInput } from "@/server/data/cx-offers"

interface PrefilledAttachment {
  /** Strapi media id — null for pass-through URLs (webhook attachments). */
  id: number | null
  name: string
  url: string
  mime: string
}

/** Minimal user shape buildSignatureContext reads — kept lite so callers can
 *  pass API-shaped agent objects without casting to the full User type. */
export interface SignatureUserLite {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  username?: string | null
  agencyDisplayName?: string | null
  phonesetup?: { phone?: string } | null
}

export interface SignatureMembershipLite {
  display_name?: string | null
  position?: string | null
}

/** Post-send side effect, executed by InlineEmailForm AFTER a successful
 *  create-outbound-mail POST (unlike pendingCxBooking, which runs BEFORE the
 *  send and mutates the body — different lifecycle, kept separate).
 *  successToast is pre-translated at dispatch time: callers own their i18n
 *  namespaces, the composer doesn't. */
export type EmailPendingAction =
  | { kind: "GD_INVOICE_SENT"; offerId: number; successToast?: string }
  | {
      kind: "GD_MAHNUNG_SENT"
      level: 1 | 2
      invoiceId: number
      offerId: number
      successToast?: string
    }
  | {
      kind: "GD_CREATE_OFFER"
      offerPayload: TSendOfferSchema & { gd_vollmacht: number | null }
      /** due as ISO string; converted to Date at execution. */
      todoPayload: {
        name: string
        lead: number
        user: number
        due: string
        type: "CUSTOM"
      } | null
      successToast?: string
    }
  | {
      kind: "CX_MARK_OFFER_SENT"
      opportunityId: number
      to?: string
      successToast?: string
    }
  | {
      kind: "CX_CREATE_OFFER"
      createPayload: OfferInput & {
        token: string
        prefilledPdfId?: number
        to?: string
      }
      successToast?: string
    }

interface EmailFormState {
  isOpen: boolean
  emailTo: string | null
  emailSubject: string | null
  emailBody: string | null
  emailLeadId: number | null
  emailUserFrom: number | null
  leadData: Lead | null
  prefilledAttachments: PrefilledAttachment[] | null
  editingEmailId: number | null
  editingEmailContent: string | null
  editingEmailPlanned: boolean
  editingEmailHappening: Date | null
  // Contact info for linking emails to contacts
  contactId: number | null
  contactInfoId: number | null
  // Preselect a template in the composer by its `type` (e.g. "CX_INFO_MAIL")
  templateType: string | null
  // Book-on-send (CX lead page): the appointment to create when this email is
  // actually sent. null for every non-CX use of the composer.
  pendingCxBooking: CreateCxAppointmentPayload | null
  // Modal-parity fields for post-action emails routed through the composer:
  plainText: string | null
  // Preselect the outbound config whose email matches (falls back to default).
  userFromEmail: string | null
  // Personalize the signature with this user/membership instead of the sender.
  signatureUser: SignatureUserLite | null
  signatureMembership: SignatureMembershipLite | null
  // Side effect to run after a successful send (status move / create-on-send).
  pendingAction: EmailPendingAction | null
}

const initialState: EmailFormState = {
  isOpen: false,
  emailTo: null,
  emailSubject: null,
  emailBody: null,
  emailLeadId: null,
  emailUserFrom: null,
  leadData: null,
  prefilledAttachments: null,
  editingEmailId: null,
  editingEmailContent: null,
  editingEmailPlanned: false,
  editingEmailHappening: null,
  contactId: null,
  contactInfoId: null,
  templateType: null,
  pendingCxBooking: null,
  plainText: null,
  userFromEmail: null,
  signatureUser: null,
  signatureMembership: null,
  pendingAction: null
}

const emailFormSlice = createSlice({
  name: "emailForm",
  initialState,
  reducers: {
    showEmailForm: (
      state,
      action: PayloadAction<{
        to?: string
        subject?: string
        body?: string
        leadId?: number
        userFrom?: number
        leadData?: Lead
        prefilledAttachments?: PrefilledAttachment[]
        contactId?: number
        contactInfoId?: number
        templateType?: string
        pendingCxBooking?: CreateCxAppointmentPayload
        plainText?: string
        userFromEmail?: string
        signatureUser?: SignatureUserLite | null
        signatureMembership?: SignatureMembershipLite | null
        pendingAction?: EmailPendingAction
      }>
    ) => {
      state.isOpen = true
      state.emailTo = action.payload.to || null
      state.emailSubject = action.payload.subject || null
      state.emailBody = action.payload.body || null
      state.emailLeadId = action.payload.leadId || null
      state.emailUserFrom = action.payload.userFrom || null
      state.leadData = action.payload.leadData || null
      state.prefilledAttachments = action.payload.prefilledAttachments || null
      state.contactId = action.payload.contactId || null
      state.contactInfoId = action.payload.contactInfoId || null
      state.templateType = action.payload.templateType || null
      state.pendingCxBooking = action.payload.pendingCxBooking || null
      state.plainText = action.payload.plainText || null
      state.userFromEmail = action.payload.userFromEmail || null
      state.signatureUser = action.payload.signatureUser || null
      state.signatureMembership = action.payload.signatureMembership || null
      state.pendingAction = action.payload.pendingAction || null
      // Reset editing state when showing new email form
      state.editingEmailId = null
      state.editingEmailContent = null
      state.editingEmailPlanned = false
      state.editingEmailHappening = null
    },
    showEditEmailForm: (
      state,
      action: PayloadAction<{
        emailId: number
        to: string
        from: string
        subject: string
        content: string
        planned: boolean
        happening: Date | null
        leadId?: number
        leadData?: Lead
      }>
    ) => {
      state.isOpen = true
      state.emailTo = action.payload.to
      state.emailSubject = action.payload.subject
      state.emailLeadId = action.payload.leadId || null
      state.emailUserFrom = null // Will be set from form
      state.leadData = action.payload.leadData || null
      state.editingEmailId = action.payload.emailId
      state.editingEmailContent = action.payload.content
      state.editingEmailPlanned = action.payload.planned
      state.editingEmailHappening = action.payload.happening
    },
    hideEmailForm: (state) => {
      state.isOpen = false
      state.emailTo = null
      state.emailSubject = null
      state.emailBody = null
      state.emailLeadId = null
      state.emailUserFrom = null
      state.leadData = null
      state.prefilledAttachments = null
      state.editingEmailId = null
      state.editingEmailContent = null
      state.editingEmailPlanned = false
      state.editingEmailHappening = null
      state.contactId = null
      state.contactInfoId = null
      state.templateType = null
      state.pendingCxBooking = null
      state.plainText = null
      state.userFromEmail = null
      state.signatureUser = null
      state.signatureMembership = null
      state.pendingAction = null
    },
    clearPendingCxBooking: (state) => {
      // Clear the pending booking WITHOUT closing the composer — used right after
      // a successful book-on-send so a send-retry can't create a second row.
      state.pendingCxBooking = null
    }
  }
})

export const { showEmailForm, showEditEmailForm, hideEmailForm, clearPendingCxBooking } =
  emailFormSlice.actions
export default emailFormSlice.reducer
